// Origin-pinned postMessage channel to the embedding page: every message targets one pinned
// origin (never '*') chosen from the session's allowed_origins.

import { isEmbedMode } from 'v2/shared/utils/embed-session'
import type { EmbedParentMessage } from './embed-protocol'

let pinnedParentOrigin: string | null = null

export const setParentOrigin = (origin: string): void => {
  pinnedParentOrigin = origin
}

// This document's pin, so another embed route mounting later keeps it.
export const getPinnedParentOrigin = (): string | undefined => pinnedParentOrigin ?? undefined

// location.ancestorOrigins (Chromium/WebKit); Firefox has none and returns null.
export const observedAncestorOrigin = (): string | null => {
  try {
    const ancestors = window.location.ancestorOrigins
    if (ancestors && ancestors.length > 0) {
      return ancestors[0] ?? null
    }
  } catch {
    // location.ancestorOrigins unsupported
  }
  return null
}

// Pins the parent origin and doubles as the render gate (null = do not render). Pins only an
// allow-listed ancestorOrigins entry or an already verified origin (the token's), never a guess.
export const resolveAndPinParentOrigin = (
  allowedOrigins: string[],
  knownParentOrigin?: string,
): string | null => {
  const observed = observedAncestorOrigin()
  if (observed) {
    if (!allowedOrigins.includes(observed)) {
      // Clear a previous pin, or postToParent would keep targeting an origin we just refused.
      pinnedParentOrigin = null
      return null
    }
    pinnedParentOrigin = observed
    return observed
  }
  if (knownParentOrigin && allowedOrigins.includes(knownParentOrigin)) {
    pinnedParentOrigin = knownParentOrigin
    return knownParentOrigin
  }
  pinnedParentOrigin = null
  return null
}

export const postToParent = (message: EmbedParentMessage): void => {
  if (!pinnedParentOrigin || window.self === window.top) {
    return
  }
  window.parent.postMessage(message, pinnedParentOrigin)
}

// Boot errors happen before any pin, so they go un-pinned. Safe: the payload is a static code and
// a generic message, never a token or tenant data.
export const postErrorToParent = (code: string, message: string): void => {
  if (window.self === window.top) {
    return
  }
  window.parent.postMessage(
    { type: 'embed:error', code, message } satisfies EmbedParentMessage,
    observedAncestorOrigin() ?? '*',
  )
}

// Pin-less 'send me a token' ping carrying no session or tenant data. `jkt` is a public-key
// thumbprint (worthless without the private key) so the partner can mint a key-bound token.
export const requestTokenFromParent = (jkt?: string): void => {
  if (window.self === window.top) {
    return
  }
  window.parent.postMessage(
    { type: 'embed:request-token', ...(jkt ? { jkt } : {}) } satisfies EmbedParentMessage,
    observedAncestorOrigin() ?? '*',
  )
}

// Acks an accepted token to its sender (no data), ending the loader's delivery deadline.
export const ackTokenToParent = (origin: string): void => {
  if (window.self === window.top) {
    return
  }
  try {
    window.parent.postMessage({ type: 'embed:token-received' } satisfies EmbedParentMessage, origin)
  } catch {
    // An opaque ('null') sender origin cannot be targeted; the loader's deadline still applies.
  }
}

// A render crash in the iframe: tell the host, so its frame does not just sit on an error panel.
export const reportEmbedRenderError = (): void => {
  if (isEmbedMode()) {
    postErrorToParent('render_error', 'Something went wrong in this view. Reload it to try again.')
  }
}
